'use client';

import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { BannerRootContext } from './context/banner-root.context';
import { bannerRecipe } from './styles/banner-root.styles';
import type { BannerRootPropsInterface } from './types/banner-root.types';

/**
 * @since 10.0.0
 */

export const BannerRoot = forwardRef<HTMLDivElement, BannerRootPropsInterface>(
  (props, forwardedRef) => {
    const {
      tone = 'info',
      children,
      className,
      css: cssProp,
      isOpen,
      onClose,
      role = 'status',
      ...rest
    } = props;

    const [openState, setOpenState] = useControllableState({
      prop: isOpen,
      defaultProp: true,
    });

    if (!openState) {
      return null;
    }

    const styles = css(bannerRecipe.raw({ tone }), cssProp);

    return (
      <BannerRootContext.Provider
        value={{
          tone,
          onClose,
          isOpen: openState,
          closeBanner: () => {
            setOpenState(false);
          },
        }}
      >
        <div
          ref={forwardedRef}
          data-c11n-component="Banner.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          role={role}
          {...rest}
        >
          {children}
        </div>
      </BannerRootContext.Provider>
    );
  },
);

BannerRoot.displayName = 'Banner.Root';
